<template>
    <CollapsibleWidget>
        <template #name>Fonts</template>

        <template #controls>
            <Tooltip content="Reset to template defaults" placement="left" transfer>
                <Icon type="md-trash" data-testid="font-style__reset" @click.exact.stop="resetFontStyles" />
            </Tooltip>
        </template>

        <div class="font-style">
            <EditableSettingsControl
                v-if="hasControl('fontFamily') && fontFamilyOptions.length"
                :title="camelToUpperCase(EditableSettingProperty.FontFamily)"
            >
                <template #button>
                    <div class="editable-settings-item">
                        <FontType width="16" height="16" />
                    </div>
                </template>

                <template #value>
                    <Select
                        v-model="fontFamily"
                        size="small"
                        clearable
                        :placeholder="placeholderValue(EditableSettingProperty.FontFamily)"
                        class="font-family font-style__control hox-select-dark"
                    >
                        <Option
                            v-for="option in fontFamilyOptions"
                            :key="option.value"
                            class="font-family__option"
                            :value="option.value"
                        >
                            {{ option.label }}
                            <span class="font-family__icon-content">
                                <span class="font-family__icon-wrapper">
                                    <span class="pretty-file-icon-html" />
                                </span>
                                <span v-if="option.postScriptName" class="font-family__icon-wrapper">
                                    <span class="pretty-file-icon-psd" />
                                </span>
                            </span>
                        </Option>
                    </Select>
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl v-if="hasControl('font')" :title="camelToUpperCase(EditableSettingProperty.Font)">
                <template #button>
                    <div class="editable-settings-item">
                        <FontType width="20" height="20" />
                    </div>
                </template>

                <template #value>
                    <EditableSettingsTextInput
                        v-model="font"
                        suffix="name"
                        :show-suffix="true"
                        :placeholder="stringPlaceholderValue(EditableSettingProperty.Font)"
                    />
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="hasControl('fontSize')"
                :title="camelToUpperCase(EditableSettingProperty.FontSize)"
            >
                <template #button>
                    <div class="editable-settings-item">
                        <LetterSize width="16" height="16" />
                    </div>
                </template>

                <template #value>
                    <EditableSettingsNumberInput
                        v-model="fontSizeInput"
                        :show-suffix="isSuffixVisible(EditableSettingProperty.FontSize)"
                        :placeholder="placeholderValue(EditableSettingProperty.FontSize)"
                    />
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="hasControl('letterSpacing')"
                :title="camelToUpperCase(EditableSettingProperty.LetterSpacing)"
            >
                <template #button>
                    <div class="editable-settings-item">
                        <LetterSpacing width="20" height="20" />
                    </div>
                </template>

                <template #value>
                    <EditableSettingsNumberInput
                        v-model="letterSpacing"
                        :show-suffix="isSuffixVisible(EditableSettingProperty.LetterSpacing)"
                        :placeholder="placeholderValue(EditableSettingProperty.LetterSpacing)"
                    />
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="hasControl('lineHeight')"
                :title="camelToUpperCase(EditableSettingProperty.LineHeight)"
            >
                <template #button>
                    <div class="editable-settings-item">
                        <LineHeight width="20" height="20" />
                    </div>
                </template>

                <template #value>
                    <EditableSettingsNumberInput
                        v-model="lineHeight"
                        :show-suffix="isSuffixVisible(EditableSettingProperty.LineHeight)"
                        :placeholder="placeholderValue(EditableSettingProperty.LineHeight)"
                    />
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="hasControl('tracking')"
                :title="camelToUpperCase(EditableSettingProperty.Tracking)"
            >
                <template #button>
                    <div class="editable-settings-item">
                        <LetterSpacing width="20" height="20" />
                    </div>
                </template>

                <template #value>
                    <EditableSettingsNumberInput
                        v-model="tracking"
                        data-testid="font-style__tracking"
                        suffix="/1000 em"
                        :show-suffix="true"
                        :min="IN_DESIGN_TRACKING_RANGE.min"
                        :max="IN_DESIGN_TRACKING_RANGE.max"
                        :placeholder="stringPlaceholderValue(EditableSettingProperty.Tracking)"
                    />
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="hasControl('kerningMethod')"
                :title="camelToUpperCase(EditableSettingProperty.KerningMethod)"
            >
                <template #button>
                    <div size="small" class="editable-settings-item font-style__control">
                        <Icon type="ios-swap" />
                    </div>
                </template>

                <template #value>
                    <Select
                        v-model="kerningMethod"
                        data-testid="font-style__kerning-method"
                        size="small"
                        clearable
                        class="hox-select-dark"
                        :placeholder="kerningMethodPlaceholder"
                    >
                        <Option v-for="option in kerningMethodOptions" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </Option>
                    </Select>
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="hasControl('kerningValue') && isManualKerning"
                :title="camelToUpperCase(EditableSettingProperty.KerningValue)"
            >
                <template #button>
                    <div class="editable-settings-item">
                        <Icon type="ios-options" />
                    </div>
                </template>

                <template #value>
                    <EditableSettingsNumberInput
                        v-model="kerningValue"
                        data-testid="font-style__kerning-value"
                        suffix="/1000 em"
                        :show-suffix="true"
                        :min="IN_DESIGN_KERNING_VALUE_RANGE.min"
                        :max="IN_DESIGN_KERNING_VALUE_RANGE.max"
                        :placeholder="stringPlaceholderValue(EditableSettingProperty.KerningValue)"
                    />
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="hasControl('textAlignment')"
                :title="camelToUpperCase(EditableSettingProperty.TextAlignment)"
            >
                <template #button>
                    <div size="small" class="editable-settings-item font-style__control">
                        <TextAlignment width="16" height="16" />
                    </div>
                </template>

                <template #value>
                    <Select
                        v-model="textAlignment"
                        size="small"
                        class="hox-select-dark"
                        :placeholder="placeholderValue(EditableSettingProperty.TextAlignment)"
                    >
                        <Option v-for="option in textAlignmentOptions" :key="option.value" :value="option.value">
                            {{ option.label }}
                        </Option>
                    </Select>
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="hasControl('fontWeight')"
                :title="camelToUpperCase(EditableSettingProperty.FontWeight)"
            >
                <template #button>
                    <div size="small" class="editable-settings-item font-style__control">
                        <FontWeight width="16" height="16" />
                    </div>
                </template>

                <template #value>
                    <Select
                        v-model="fontWeight"
                        size="small"
                        class="hox-select-dark"
                        :placeholder="fontWeightPlaceholderValue"
                    >
                        <Option v-for="option in fontWeightOptions" :key="option" :value="option">
                            {{ option }}
                        </Option>
                    </Select>
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="hasControl('fontStyle')"
                :title="camelToUpperCase(EditableSettingProperty.FontStyle)"
            >
                <template #button>
                    <div size="small" class="editable-settings-item font-style__control">
                        <FontItalic width="16" height="16" />
                    </div>
                </template>

                <template #value>
                    <Select
                        v-model="fontStyle"
                        size="small"
                        class="hox-select-dark"
                        :placeholder="fontStylePlaceholderValue"
                    >
                        <Option v-for="option in fontStyleOptions" :key="option" :value="option">
                            {{ option }}
                        </Option>
                    </Select>
                </template>
            </EditableSettingsControl>

            <EditableSettingsControl
                v-if="hasControl('fontColor')"
                :title="camelToUpperCase(EditableSettingProperty.FontColor)"
            >
                <template #row>
                    <div class="font-style__control--color-row">
                        <ColorPicker
                            ref="picker"
                            v-model="fontColor"
                            class="font-style__control--color-picker"
                            size="small"
                            transfer
                        />
                        <div class="font-style__control--color-value">
                            {{ fontColorDisplay }}
                        </div>
                    </div>
                </template>
            </EditableSettingsControl>
        </div>
    </CollapsibleWidget>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import type { CSSSettings } from "shared-types";
import rgbToHex from "shared-utils/utils/rgbToHex";

import LetterSize from "@/assets/text-editor/letter-size.svg";
import LetterSpacing from "@/assets/text-editor/spacing.svg";
import LineHeight from "@/assets/text-editor/leading.svg";
import FontWeight from "@/assets/text-editor/bold-text.svg";
import TextAlignment from "@/assets/text-editor/text-align.svg";
import FontItalic from "@/assets/text-editor/italics.svg";
import FontType from "@/assets/text-editor/type.svg";

import EditableSettingsControl from "@/components/Campaign/SettingsPane/EditableSettingsControl.vue";
import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";
import EditableSettingsTextInput from "@/components/Campaign/SettingsPane/EditableSettingsTextInput.vue";
import { IN_DESIGN_KERNING_VALUE_RANGE, IN_DESIGN_TRACKING_RANGE } from "shared-utils/enums/indesign";
import EditableSettingsNumberInput from "@/components/Campaign/SettingsPane/EditableSettingsNumberInput.vue";

import { useEditableSettings } from "@/composables/useEditableSettings";
import { useEditor } from "@/composables/useEditor";

import { camelToUpperCase } from "@/utils";
import { EditablePhotoshopSettings, EditableSettingProperty } from "@/enums/editables";

const emptyPropertyText = "Not set";
const emptySelect = "Select";
const fontWeightOptions = ["normal", "bold"];
const fontStyleOptions = ["normal", "italic"];
const kerningMethodOptions = [
    { label: "Metrics", value: "METRICS" },
    { label: "Optical", value: "OPTICAL" },
    { label: "Manual", value: "MANUAL" }
];
const textAlignmentOptions = [
    { label: "Left", value: "left" },
    { label: "Center", value: "center" },
    { label: "Right", value: "right" },
    { label: "Justify Left", value: "justifyLeft" },
    { label: "Justify Center", value: "justifyCenter" },
    { label: "Justify Right", value: "justifyRight" },
    { label: "Justify All", value: "justifyAll" }
];

type FontStyleControl =
    | "font"
    | "fontFamily"
    | "fontSize"
    | "letterSpacing"
    | "lineHeight"
    | "textAlignment"
    | "fontWeight"
    | "fontStyle"
    | "fontColor"
    | "tracking"
    | "kerningMethod"
    | "kerningValue";

const props = withDefaults(
    defineProps<{
        computedStyle?: CSSStyleDeclaration;
        boundingClientRect?: DOMRect;
        controls?: FontStyleControl[];
    }>(),
    {
        controls: () => [
            "fontFamily",
            "fontSize",
            "letterSpacing",
            "lineHeight",
            "fontWeight",
            "fontStyle",
            "fontColor"
        ]
    }
);

const emit = defineEmits<(e: "onSettingsChange", value: CSSSettings | null) => void>();

const onSettingsChange = (settings: CSSSettings | null) => {
    emit("onSettingsChange", settings);
};

const { isCampaignPSD, fonts, fontIds } = useEditor();

const {
    fontSize,
    fontColor,
    fontFamily,
    fontStyle,
    fontWeight,
    letterSpacing,
    lineHeight,
    textAlignment,
    font,
    tracking,
    kerningMethod,
    kerningValue,

    isSuffixVisible,
    getPlaceholderValue,
    getInheritedValue,
    getPsdPlaceholderValue
} = useEditableSettings({
    onSettingsChange,
    boundingClientRect: props.boundingClientRect,
    computedStyle: props.computedStyle
});

// A size can inherit Manual from the group and still needs somewhere to set its own value
// || not ??: clearing the select passes through ""
const effectiveKerningMethod = computed(() => kerningMethod.value || getInheritedValue("kerningMethod"));
const isManualKerning = computed(() => effectiveKerningMethod.value === "MANUAL");

const kerningMethodPlaceholder = computed(() => {
    const inherited = getInheritedValue("kerningMethod");

    return kerningMethodOptions.find(option => option.value === inherited)?.label ?? emptySelect;
});

const fontColorDisplay = computed(() => {
    if (fontColor.value === undefined) {
        if (isCampaignPSD.value) {
            const { rgb } = getPsdPlaceholderValue("fontColor") as {
                rgb: {
                    red?: number;
                    green?: number;
                    blue?: number;
                };
            };

            if (rgb?.green !== undefined && rgb?.red !== undefined && rgb?.blue !== undefined) {
                return rgbToHex(rgb.red, rgb.green, rgb.blue, true);
            }
        } else {
            return getPlaceholderValue("fontColor") || emptyPropertyText;
        }
    }
    return fontColor.value;
});

const fontSizeInput = computed({
    get() {
        if (fontSize.value === undefined) {
            return undefined;
        }

        const value = Number(fontSize.value);

        return Number.isNaN(value) ? undefined : value;
    },

    set(value: number | undefined) {
        fontSize.value = value === undefined ? undefined : value.toString();
    }
});

const fontFamilyOptions = computed(() =>
    fontIds.value.flatMap(fontId => {
        const font = fonts.value[fontId];

        if (!font) {
            return [];
        }

        const { _id: value, name: label, postScriptName } = font;

        return [{ label, value, postScriptName }];
    })
);

const fontStylePlaceholderValue = computed(() => getPlaceholderValue("fontStyle") || emptySelect);

const fontWeightPlaceholderValue = computed(() => {
    const value = isCampaignPSD.value ? getPsdPlaceholderValue("fontWeight") : getPlaceholderValue("fontWeight");

    switch (value) {
        case "400":
            return "normal";
        case "700":
            return "bold";
        default:
            return value || emptySelect;
    }
});

const resetters: Record<FontStyleControl, () => void> = {
    font: () => {
        font.value = undefined;
    },
    fontFamily: () => {
        fontFamily.value = undefined;
    },
    fontSize: () => {
        fontSize.value = undefined;
    },
    letterSpacing: () => {
        letterSpacing.value = undefined;
    },
    lineHeight: () => {
        lineHeight.value = undefined;
    },
    textAlignment: () => {
        textAlignment.value = undefined;
    },
    fontWeight: () => {
        fontWeight.value = undefined;
    },
    fontStyle: () => {
        fontStyle.value = undefined;
    },
    fontColor: () => {
        fontColor.value = undefined;
    },
    tracking: () => {
        tracking.value = undefined;
    },
    kerningMethod: () => {
        kerningMethod.value = undefined;
    },
    kerningValue: () => {
        kerningValue.value = undefined;
    }
};

function hasControl(control: FontStyleControl) {
    return props.controls.includes(control);
}

function resetFontStyles() {
    props.controls.forEach(control => resetters[control]?.());
}

function stringPlaceholderValue(prop: keyof CSSSettings) {
    return getPlaceholderValue(prop)?.toString() ?? "";
}

function placeholderValue(prop: keyof typeof EditablePhotoshopSettings) {
    if (isCampaignPSD.value) {
        return getPsdPlaceholderValue(EditablePhotoshopSettings[prop]).toString();
    }

    return stringPlaceholderValue(prop);
}
</script>

<style lang="scss">
@use "@style/variables" as *;

.font-style {
    &__control {
        &--color-value {
            position: absolute;
            width: calc(100% - 40px);
            z-index: 1;
            top: 0;
            margin-left: 40px;
            padding: 0 10px;
            border-left: 1px solid $cmp-dark-bg-color;
        }

        &--color-row {
            position: relative;
            width: 100%;
        }

        &--color-picker {
            cursor: pointer;
            position: relative;
            width: 100%;
            z-index: 2;

            .ivu-input-icon {
                display: none;
            }

            .ivu-input {
                border: transparent 0;
                background-color: inherit;
                width: 140px;

                &:hover,
                &:active {
                    border: transparent 0;
                    cursor: pointer;
                }
            }

            .ivu-input-icon-normal + .ivu-input {
                padding: 2px 6px;
                width: 40px;
                height: 33px;
            }

            .ivu-color-picker-small .ivu-color-picker-color {
                margin: 4px 6px;
            }
        }
    }

    &__color-value {
        width: 100%;
        height: 100%;
        cursor: pointer;
        background-color: green;
    }
}

.font-family {
    max-width: 186px;
    &__option {
        padding-right: 100px;
        position: relative;
    }

    &__icon-content {
        display: flex;
        padding-left: 5px;
    }

    &__icon-wrapper {
        display: inline-block;
        width: 24px;
        height: 24px;
        filter: grayscale(100%);
    }

    .ivu-select-dropdown-list {
        padding-left: 0;
    }

    .ivu-select-dropdown {
        max-width: 186px !important;
    }

    .ivu-select-item {
        padding: 7px 10px;
        display: flex;
        align-items: center;
        white-space: normal;
    }
}
</style>
